import { ImagePaths } from '../../lib/imageUrls.js';

export const sc300IdentityAccessAdministrator = {
  id: 31,
  slug: 'sc-300-identity-access-administrator',
  ogImage: '/og/courses/sc-300-identity-access-administrator.jpg',
  courseCode: 'SC-300-001',
  featured: false,
  title: 'SC-300 Microsoft Identity and Access Administrator',
  subtitle: 'Master Microsoft Entra ID and prepare for the SC-300 certification exam',

  valueProposition: 'Design, implement and operate Microsoft Entra ID at enterprise scale. This intensive programme covers the full SC-300 exam blueprint — identities, authentication, app access and identity governance — mapped to Australian Signals Directorate Essential Eight controls and OAIC Australian Privacy Principles so your identity platform is both certification-ready and audit-ready.',

  duration: '32 hours (4 days intensive or 8 weeks part-time)',
  students: 'New',
  level: 'Intermediate',
  organization: 'Microsoft',
  image: ImagePaths.COURSE_1,
  shortDescription: 'SC-300-001 — Microsoft Entra ID identity engineering covering the SC-300 exam blueprint, conditional access, and governance for Australian identity engineers.',

  price: {
    aud: 1499,
    currency: 'AUD',
    earlyBird: 1199
  },

  stripeConfig: {

    productId: 'prod_UarsgHWVJBCr1g',

    priceId: 'price_1TikaKKu1nyc322Uw9ONUujw'

  },

  snapshot: {
    whoItsFor: [
      'Identity and security engineers preparing for the Microsoft SC-300 certification exam',
      'IT managers running Microsoft Entra ID for Australian organisations',
      'Microsoft 365 administrators expanding into identity governance',
      'Security architects implementing Zero Trust and conditional access at scale'
    ],
    accreditation: 'Preparation for Microsoft Certified: Identity and Access Administrator Associate (Exam SC-300)'
  },

  logistics: {
    classSize: 'Maximum 12 participants for guided lab work and exam coaching',
    requirements: [
      'Working knowledge of Microsoft 365 and Azure fundamentals (AZ-900 or SC-900 level)',
      'Active Microsoft Entra ID tenant (developer or trial subscription acceptable)',
      'Administrator workstation with modern browser, PowerShell 7 and Microsoft Graph PowerShell SDK',
      'Reliable internet connection (minimum 20 Mbps) for live labs'
    ]
  },

  overview: 'The SC-300 Microsoft Identity and Access Administrator programme prepares experienced administrators to design, build and operate identity solutions on Microsoft Entra ID. Across 32 hours of guided instruction and labs, participants work through every domain of the current SC-300 exam blueprint — implementing identities, configuring authentication and access, integrating applications, and operating identity governance. The curriculum is delivered in the Australian context: every lab references Essential Eight mitigation strategies (particularly Multi-Factor Authentication, Restrict Administrative Privileges and User Application Hardening), the ACSC Information Security Manual identity controls, and OAIC Australian Privacy Principles 6, 8 and 11 covering collection, use and protection of identity data. The programme also addresses the migration context for organisations whose engineers were previously certified on the retired MS-100 and MS-101 exams, which Microsoft retired on 30 September 2023 in favour of role-based identity, security and modern work paths.',

  outcomes: [
    'Implement and manage Microsoft Entra ID tenants, users, groups and external identities at enterprise scale',
    'Design conditional access policies and multi-factor authentication aligned to Essential Eight Maturity Levels',
    'Configure passwordless authentication using Windows Hello for Business, FIDO2 keys and Microsoft Authenticator',
    'Integrate SaaS and line-of-business applications using SAML, OIDC and SCIM provisioning',
    'Operate Privileged Identity Management (PIM), access reviews and entitlement management for least-privilege access',
    'Investigate identity risk using Entra ID Protection, sign-in logs and Microsoft Graph activity APIs',
    'Map identity controls to ACSC ISM, Essential Eight and OAIC APP obligations for Australian audits',
    'Pass the Microsoft SC-300 exam with structured practice questions and timed mock exams'
  ],

  audience: [
    'Identity and access engineers operating Microsoft Entra ID in Australian organisations',
    'Microsoft 365 administrators expanding into conditional access and identity governance',
    'Security architects implementing Zero Trust access models at enterprise scale',
    'Engineers previously certified on the retired MS-100 and MS-101 exams seeking a current credential',
    'IT managers accountable for identity audit evidence under the ACSC ISM and OAIC APPs',
    'Consultants delivering Entra ID rollouts and privileged access programmes for clients'
  ],

  prerequisites: [
    'Working knowledge of Microsoft 365 and Azure fundamentals (AZ-900 or SC-900 level)',
    'Hands-on experience administering users, groups and licences in a Microsoft 365 tenant',
    'An active Microsoft Entra ID tenant with Global Administrator rights (developer or trial acceptable)',
    'Administrator workstation with PowerShell 7 and the Microsoft Graph PowerShell SDK installed',
    'Basic familiarity with authentication protocols (SAML, OpenID Connect) is helpful but not required'
  ],

  modules: [
    {
      number: 1,
      title: 'Implement Identities in Microsoft Entra ID',
      duration: '6 hours',
      topics: [
        'Configure and manage Microsoft Entra ID tenants, custom domains and company branding',
        'Create and manage users, groups, administrative units and licences',
        'Configure external identities, B2B collaboration and cross-tenant access settings',
        'Implement hybrid identity with Microsoft Entra Connect Sync and Cloud Sync',
        'Map identity data handling to OAIC APP 6 and APP 11 obligations'
      ]
    },
    {
      number: 2,
      title: 'Implement Authentication and Access Management',
      duration: '6 hours',
      topics: [
        'Configure multi-factor authentication policies aligned to Essential Eight MFA mitigation',
        'Deploy passwordless methods: Windows Hello for Business, FIDO2, Microsoft Authenticator',
        'Design and implement Conditional Access policies and authentication strengths',
        'Configure self-service password reset, smart lockout and password protection',
        'Investigate sign-in and audit logs using Microsoft Graph and Log Analytics'
      ]
    },
    {
      number: 3,
      title: 'Plan and Implement Access Management for Applications',
      duration: '6 hours',
      topics: [
        'Register and manage enterprise applications and app registrations',
        'Configure SAML, OpenID Connect and OAuth 2.0 single sign-on',
        'Implement SCIM-based user provisioning to SaaS applications',
        'Manage API permissions, consent frameworks and admin consent workflow',
        'Monitor and remediate risky OAuth applications via Microsoft Defender for Cloud Apps'
      ]
    },
    {
      number: 4,
      title: 'Plan and Implement Identity Governance',
      duration: '6 hours',
      topics: [
        'Configure entitlement management, access packages and connected organisations',
        'Implement Privileged Identity Management (PIM) for Entra and Azure roles',
        'Design and run access reviews for users, guests and privileged roles',
        'Configure lifecycle workflows for joiner-mover-leaver scenarios',
        'Align governance to ACSC ISM identity and access management controls'
      ]
    },
    {
      number: 5,
      title: 'Identity Protection, Monitoring and Incident Response',
      duration: '4 hours',
      topics: [
        'Configure Microsoft Entra ID Protection user risk and sign-in risk policies',
        'Integrate identity telemetry with Microsoft Sentinel and Defender XDR',
        'Investigate token theft, password spray and consent phishing incidents',
        'Implement break-glass accounts and emergency access procedures',
        'Notifiable Data Breaches scheme reporting workflow for identity incidents'
      ]
    },
    {
      number: 6,
      title: 'SC-300 Exam Preparation and Capstone',
      duration: '4 hours',
      topics: [
        'Walk-through of the current SC-300 exam blueprint and skills measured',
        'Context for engineers transitioning from the retired MS-100 and MS-101 exams',
        'Timed practice exams with detailed answer explanations',
        'Capstone lab: design an end-to-end Entra ID rollout for a 1,500-user Australian organisation',
        'Exam day logistics, Pearson VUE and Microsoft Certification dashboard guidance'
      ]
    }
  ],

  format: {
    type: 'Live online training with guided labs — 4 days intensive or 8 weeks part-time',
    includes: [
      '32 hours of instructor-led instruction with guided Entra ID lab work',
      'Tenant setup scripts and a starter configuration so labs run within 30 minutes of day one',
      'Conditional Access and authentication strength policy templates mapped to Essential Eight MFA',
      'Privileged Identity Management, access review and lifecycle workflow runbooks',
      'Capstone lab: design an end-to-end Entra ID rollout for a 1,500-user Australian organisation',
      'Timed SC-300 practice exams with detailed answer explanations',
      'Recorded sessions and lab guides for 6 months',
      'Certificate of completion and exam registration walk-through'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Four-day intensive cohorts run monthly; the 8-week part-time stream runs one evening per week. Private on-site cohorts available in Sydney, Melbourne, Brisbane, Canberra and Perth'
  },

  certification: 'Prepares participants for Microsoft Certified: Identity and Access Administrator Associate via Exam SC-300. Exam voucher purchased separately through Microsoft Learn or Pearson VUE.',

  faqs: [
    {
      question: 'What is the current status of the SC-300 exam?',
      answer: 'SC-300 is an active Microsoft role-based certification. Microsoft maintains the official exam page at learn.microsoft.com/credentials/certifications/exams/sc-300, where the latest skills outline and study guide are published. We refresh course content within 30 days of any blueprint update.'
    },
    {
      question: 'I was previously certified on MS-100 or MS-101 — is SC-300 the right successor?',
      answer: 'Microsoft retired the MS-100 and MS-101 exams on 30 September 2023. For identity-focused engineers, SC-300 is the recommended role-based replacement, alongside MS-102 for broader Microsoft 365 administration. The course explicitly bridges concepts from the retired exams to the current SC-300 blueprint.'
    },
    {
      question: 'Does the course cover Australian compliance frameworks?',
      answer: 'Yes. Every module maps identity controls to the ACSC Essential Eight (particularly MFA, Restrict Administrative Privileges and Patch Applications), the ACSC Information Security Manual identity guidelines, and the OAIC Australian Privacy Principles relevant to identity data — primarily APP 6, APP 8 and APP 11.'
    },
    {
      question: 'What hands-on environment do I need?',
      answer: 'A Microsoft Entra ID tenant — either a paid M365 tenant where you hold Global Administrator rights, or a free Microsoft 365 Developer tenant. We provide setup scripts and a starter configuration so labs are ready within 30 minutes of day one.'
    },
    {
      question: 'Is the SC-300 exam voucher included in the course fee?',
      answer: 'No. The exam voucher (approximately AUD 247 at current Microsoft pricing) is purchased separately through Pearson VUE or Microsoft Learn. We provide a registration walk-through during the final module and share current Microsoft exam discount programmes where available.'
    },
    {
      question: 'Can the course be delivered in-house for our identity team?',
      answer: 'Yes. We deliver private cohorts on-site in Sydney, Melbourne, Brisbane, Canberra and Perth, or via dedicated live online sessions. Private cohorts can extend the capstone to model your real tenant architecture under NDA.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'OAIC APPs']
};
